import { 
    setupFormCommonBehaviour,
} from ".";

import { 
    InputNameConfigMap,
    FileUploadItemError,
    uploadAttachments,
} from "./file-upload";

declare const jQuery: any;
const $ = jQuery;

const ajaxAction: string = "supp-acc-update-post";
const uploadHandler: string = "supp-acc-attachment"; // PHP FileUpload class NAME const

const ajaxCall: Function = window["iw"].utils.ajaxCall;
const errorModal: Function = window["iw"].modal.errorModal;
const confirmModal: Function = window["iw"].modal.confirmModal;
const successModal: Function = window["iw"].modal.successModal;
const waitModal: Function = window["iw"].modal.waitModal;
const closeModal: Function = window["iw"].modal.closeModal;
const initCollapsibleSections: Function = window["iw"].initCollapsibleSections;
const reinitWysiwygs: Function = window["iw"].reinitWysiwygs;
const scrollToWithHeaderOffset: Function = window["iw"].layout.scrollToWithHeaderOffset;
const initCustomCheckboxes: Function = window["iw"].initCustomCheckboxes;
const initCustomSelects: Function = window["iw"].initCustomSelects;

export function initEditPost($main): void
{
    const $mainInner = $main.children(`[data-edit-post]`);
    if ($mainInner.length === 0)
    {
        return;
    }

    const postId: number = parseInt($mainInner.attr("data-edit-post"), 10);
    if (isNaN(postId))
    {
        throw new Error(`No postId`);
    }

    const previewUrl: string = $mainInner.attr("data-preview-url");
    const approvalRequested: boolean = !!$mainInner.attr("data-approval-requested");
    
    initMainInner($mainInner, postId, previewUrl, approvalRequested);
}

function initMainInner($mainInner, postId: number, previewUrl: string, approvalRequested: boolean): void
{
    const $save = $mainInner.find("[data-main-buttons] [data-save]");
    const $preview = $mainInner.find("[data-main-buttons] [data-preview]");
    const $publish = $mainInner.find("[data-main-buttons] [data-publish]");
    const $addAnother = $mainInner.find("[data-add-another-wrap] a");

    let unsavedChanges = false;

    const disableAddAnother = () => {
        $addAnother.attr("disabled", "disabled").on("click", function (event) {
            if (!confirm(`You have unsaved changes. Do you want to discard them and add a new post?`)) {
                event.preventDefault();
                return false;
            }
            commonCtrls.cancelWarnBeforePageLeave();
        });
    };

    const onFirstChange = () => {
        $save.removeAttr("disabled");
        $publish.removeAttr("disabled");
        $preview.attr("disabled", "disabled");
        disableAddAnother();
        unsavedChanges = true;
    };

    const commonCtrls = setupFormCommonBehaviour($mainInner, onFirstChange);

    // 4-stage server request process:
    // (1) Create post (if not yet created) and update non-file-related post data
    // (2) Upload file attachments
    // (3) Update post with secondary (file-related) post data
    // (4) Get updated form HTML and render/initialize
    
    const buildBasicAjaxData = (rawData, requestApproval: boolean) => {
        return {
            request_approval: requestApproval,
            post_id: postId,
            post_title: rawData.post_title,
            post_content: rawData.post_content
        };
    };

    const saveBasicAjaxData = async (basicAjaxData): Promise<{ postId: number }> => {
        waitModal(`Submitting news post, please wait...`);
        const response = await ajaxCall(ajaxAction, basicAjaxData);
        if (!response?.postId) {
            console.error(`saveBasicAjaxData - bad response:`, response);
            throw new Error(`No response.postId`);
        }
        return response;
    };

    const uploadPostAttachments = async (rawData, newPostId: number): Promise<void> => {
        const uploadData = { post_id: newPostId };
        const inputNameConfigMap: InputNameConfigMap = {
            post_featured_image: { inputType: "images", progressMsg: `Uploading banner image...` }
        };
        await uploadAttachments(uploadHandler, rawData, inputNameConfigMap, uploadData);
    };

    const buildSecondaryAjaxData = (rawData, newPostId: number) => {
        return {
            post_id: newPostId,
            post_featured_image: rawData.post_featured_image
        };
    };

    const saveSecondaryAjaxData = async (secondaryAjaxData): Promise<{ postId: number }> => {
        waitModal(`Saving image data, please wait...`);
        const response = await ajaxCall(ajaxAction, secondaryAjaxData);
        if (!response?.postId) {
            console.error(`saveSecondaryAjaxData - bad response:`, response);
            throw new Error(`No response.postId`);
        }
        return response;
    };

    const getUpdatedMainInnerHtml = async (): Promise<{ html: string, previewUrl: string }> => {
        waitModal(`Preparing preview, please wait...`);
        const response = await ajaxCall("supp-acc-get-form", { form: "edit_post", post_id: postId });
        if (!response?.html || !response?.previewUrl) {
            console.error(`getUpdatedMainInnerHtml - bad response:`, response);
            throw new Error(`No response.html/response.previewUrl`);
        }
        return response;
    };

    const save = async (requestApproval: boolean) => {
        const rawData = commonCtrls.gatherData();
        if (!rawData)
        {
            // User data invalid. Error modal has already been displayed.
            return;
        }
        const basicAjaxData = buildBasicAjaxData(rawData, requestApproval);
        if (!basicAjaxData)
        {
            throw new Error(`No basicAjaxData`);
        }
        $mainInner.addClass("busy");
        try
        {
            const basicAjaxResponse = await saveBasicAjaxData(basicAjaxData);
            if (!basicAjaxResponse?.postId)
            {
                throw new Error(`No basicAjaxResponse.postId`);
            }
            const newPostId: number = basicAjaxResponse.postId;
            if (!postId)
            {
                window.history.replaceState(null, null, `?edit-post=${newPostId}`);
            }
            postId = newPostId;
            await uploadPostAttachments(rawData, newPostId);
            await saveSecondaryAjaxData(buildSecondaryAjaxData(rawData, newPostId));
            const response = await getUpdatedMainInnerHtml();
            commonCtrls.cancelWarnBeforePageLeave();
            $mainInner.html($(response.html).html());
            reinitWysiwygs($mainInner.find(`[data-wysiwyg]`));
            initCollapsibleSections();
            initCustomCheckboxes();
            initCustomSelects();
            initMainInner($mainInner, newPostId, response.previewUrl, requestApproval);
            scrollToWithHeaderOffset($mainInner);
        }
        catch (error)
        {
            if (!(error instanceof FileUploadItemError)) // FileUploadItemError already shows modal before throw
            {
                errorModal(`Failed to save. If the problem persists, please get in touch.`);
            }
            console.error("Save failed:", error);
            return;
        }
        finally
        {
            $mainInner.removeClass("busy");
        }
        successModal(
            requestApproval ? `Your submission has been received and is awaiting approval.` : `Your news post has been saved.`,
            undefined,
            5000
        );
    };

    $save.on("click", function (event) {
        save(false);
        return false;
    });

    $publish.on("click", function (event) {
        save(true);
        return false;
    });

    if (previewUrl)
    {
        $preview.removeAttr("disabled").on("click", function (event) {
            const checkAndNavigate = async () => {
                if (unsavedChanges &&
                    !await confirmModal("Preview News Post", "You have unsaved changes which will not show up on the preview. View preview anyway?", "yesno"))
                {
                    return;
                }
                window.open(previewUrl);
            };
            checkAndNavigate();
            return false;
        });
    }
    else
    {
        $preview.attr("title", "You must save before you can preview");
    }

    if (!approvalRequested)
    {
        $publish.removeAttr("disabled");
    }
}
